<template>
    <div>
        <Head title="Challenge Request" />

        <heading class="">
            <template #heading>
                <div class="flex items-center">
                    <Link :href="route('challengeRequests.index', { post: post })" class="-m-px p-2 hover:text-blue-500 focus:text-blue-600 hover:bg-white focus:bg-white rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
                        </svg>
                    </Link>
                    <span class="ml-2">Challenge Request</span>
                </div>
            </template>
        </heading>

        <div class="lg:grid lg:gap-4 lg:grid-cols-12">
            <div class="flex flex-col space-y-4 lg:col-span-8 lg:px-0">
                <div class="md:hidden flex justify-center mt-4 px-3 space-x-4">
                    <!-- Banner indicating the decision -->
                    <div v-if="isApproved" class="w-full px-4 py-5 text-xl bg-green-500 text-white text-center rounded-lg shadow">Approved</div>
                    <div v-if="isDenied" class="w-full px-4 py-5 text-xl bg-red-500 text-white text-center rounded-lg shadow">Denied</div>

                    <!-- Buttons - Only display if neither approved nor denied -->
                    <jet-button @click.native="approveCompetitor(competitor.id)" class="flex-1" color="green" v-if="!isApproved && !isDenied">Approve</jet-button>
                    <jet-button @click.native="denyCompetitor(competitor.id)" class="px-4 py-2 flex-1" color="red" v-if="!isApproved && !isDenied">Deny</jet-button>
                </div>
                <div class="flex flex-row space-y-8">
                    <video-player class="rounded-lg overflow-hidden" v-if="playlistUrl" :options="videoPlayerOptions"></video-player>
                </div>
                <!-- Buttons - Only display if neither approved nor denied -->
                <div class="md:flex hidden justify-center mt-4 pb-16 px-3 space-x-4">
                    <!-- Banner indicating the decision -->
                    <div v-if="isApproved" class="w-full px-4 py-5 text-xl bg-green-500 text-white text-center rounded-lg shadow">Approved</div>
                    <div v-if="isDenied" class="w-full px-4 py-5 text-xl bg-red-500 text-white text-center rounded-lg shadow">Denied</div>

                    <!-- Buttons - Only display if neither approved nor denied -->
                    <jet-button @click.native="approveCompetitor(competitor.id)" class="flex-1" color="green" v-if="!isApproved && !isDenied">Approve</jet-button>
                    <jet-button @click.native="denyCompetitor(competitor.id)" class="px-4 py-2 flex-1" color="red" v-if="!isApproved && !isDenied">Deny</jet-button>
                </div>
            </div>

            <!-- Aside -->
            <div class="hidden lg:block lg:col-span-4">
                <div class="sticky top-8 space-y-4">
                    <div class="px-4 py-6 bg-white rounded-lg shadow">
                        <div class="block hover:opacity-75 space-y-4">
                            <div class="flex flex-col justify-center align-middle text-center">
                                <div class="flex justify-center mb-1 mr-1 text-pink-500 text-6xl font-bold">
                                    <span class="currency font-display">{{ post.pledgedAmount | abbr }}</span>
                                </div>
                                <div class="text-pink-300 text-xs font-semibold uppercase">value of tokens pledged</div>
                            </div>

                            <!-- Supporter Stats -->
                            <div class="grid gap-1 grid-cols-3 text-center">
                                <div class="col-span-1">
                                    {{ post.pledgesCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "pledge" | pluralize(post.pledgesCount) }}
                                    </div>
                                </div>
                                <div class="col-span-1">
                                    {{ post.likesCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">{{ "like" | pluralize(post.likesCount) }}</div>
                                </div>
                                <div class="col-span-1">
                                    {{ post.commentsCount | abbr }}
                                    <div class="text-gray-500 text-xs uppercase">
                                        {{ "comment" | pluralize(post.commentsCount) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Link :href="post.showUrl" class="group block bg-white rounded-lg shadow space-y-4">
                        <!-- Featured Image -->
                        <div class="aspect-w-10 aspect-h-7 block p-0 w-full h-48 shadow-inner group-hover:opacity-75 sm:rounded-t-lg sm:overflow-hidden" v-html="post.featuredImageHtml"></div>

                        <!-- Post Details -->
                        <div class="pb-3 px-4">
                            <div class="line-clamp-3 group-hover:text-gray-500 text-gray-700 text-sm" v-html="post.html"></div>

                            <button class="flex items-center pt-3 group-hover:text-blue-500 text-gray-500 text-sm">
                                <svg class="-ml-1 mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M15 19l-7-7 7-7" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" />
                                </svg>
                                <span>Go to challenge</span>
                            </button>
                        </div>
                    </Link>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout"
import AppHead from "@/Components/AppHead"
import AppLink from "@/Components/AppLink"
import Heading from "@/Components/Heading"
import Tooltip from "@/Components/Tooltip"
import JetButton from "@/Jetstream/Button"
import MediaModal from "@/Components/MediaModal.vue"
import VideoPlayer from "@/Components/VideoPlayer"

export default {
    components: { MediaModal, AppLink, Tooltip, Heading, AppHead, JetButton, VideoPlayer },
    layout: AppLayout,

    props: {
        post: {
            required: true,
            type: Object,
        },
        competitor: {
            required: true,
            type: Object,
        },
    },

    computed: {
        playlistUrl() {
            if (!this.competitor.video) return false
            return this.competitor.video.playlistUrl
        },

        videoPlayerOptions() {
            return {
                sources: [{ src: this.playlistUrl }],
                fluid: true,
                autoplay: false,
                aspectRatio: this.competitor.video.formattedAspectRatio,
                poster: this.competitor.video.frames[0],
            }
        },

        // Check if video is approved
        isApproved() {
            return this.competitor.video && this.competitor.video.approved === 1
        },

        // Check if video is denied
        isDenied() {
            return this.competitor.video && this.competitor.video.approved === 0
        },
    },

    data() {
        return {
            showVideoPlayer: false,
            approveCompetitorForm: this.$inertia.form({
                _method: "POST",
            }),
            denyCompetitorForm: this.$inertia.form({
                _method: "POST",
            }),
        }
    },

    methods: {
        approveCompetitor(competitorId) {
            this.approveCompetitorForm.post(route("competitors.approve", competitorId), {
                errorBag: "approveCompetitor",
                preserveScroll: true,
                onSuccess: () => {},
            })
        },

        denyCompetitor(competitorId) {
            this.denyCompetitorForm.post(route("competitors.deny", competitorId), {
                errorBag: "denyCompetitor",
                preserveScroll: true,
                onSuccess: () => {},
            })
        },
    },
}
</script>
